import {
  type DiffLine,
  type DiffRow,
  type DiffSegment,
  diffText,
  foldUnchanged,
  sameDraftText,
} from "@/lib/text-diff";
import { cn } from "@/lib/utils";
import { Fragment, type ReactNode, useMemo, useState } from "react";

/** What differs between a removed and an added row. The colors are the
 * `--draft-diff-*` tokens of `styles/index.css`, read through the `color:` hint
 * because Tailwind cannot tell a color from a font size in `text-[var(--x)]`. */
const SIDE = {
  removed: {
    mark: "−",
    spoken: "Eliminado: ",
    markerName: "eliminado",
    row: "bg-[color:var(--draft-diff-removed-bg)] text-[color:var(--draft-diff-removed-fg)]",
    word: "bg-[color:var(--draft-diff-removed-word-bg)] text-[color:var(--draft-diff-removed-word-fg)] line-through",
  },
  added: {
    mark: "+",
    spoken: "Agregado: ",
    markerName: "agregado",
    row: "bg-[color:var(--draft-diff-added-bg)] text-[color:var(--draft-diff-added-fg)]",
    word: "bg-[color:var(--draft-diff-added-word-bg)] text-[color:var(--draft-diff-added-word-fg)] underline",
  },
} as const;

const ROW_CLASS = "flex gap-2 px-2";
const MARK_CLASS = "w-3 shrink-0 select-none text-center";
const TEXT_CLASS = "min-w-0 flex-1 whitespace-pre-wrap [overflow-wrap:anywhere]";

interface DraftDiffProps {
  /** The previous text. Callers pass `""` for a field that did not exist. */
  before: string;
  /** The new text. */
  after: string;
  /** The old side in the legend and the too-large fallback. */
  beforeLabel?: string;
  /** The new side. */
  afterLabel?: string;
  /** Accessible name of the diff list. */
  label?: string;
}

/** A row of the shown diff. A fold knows where its hidden lines start in the OLD
 * text: that is what identifies it while the new text changes under it. */
type ViewRow =
  | Extract<DiffRow, { kind: "line" }>
  | (Extract<DiffRow, { kind: "fold" }> & { start: number });

/** What the component shows for one pair of texts. */
type View = { kind: "too-large" } | { kind: "same" } | { kind: "rows"; rows: readonly ViewRow[] };

/** The rows with each fold carrying the index, in the old text, of its first hidden
 * line. Added lines are not in the old text; every other line, hidden ones included, is. */
function withFoldStarts(rows: readonly DiffRow[]): ViewRow[] {
  let oldLines = 0;
  return rows.map((row) => {
    if (row.kind === "line") {
      if (row.line.type !== "added") oldLines += 1;
      return row;
    }
    const start = oldLines;
    oldLines += row.hidden.length;
    return { ...row, start };
  });
}

function viewOf(before: string, after: string): View {
  const diff = diffText(before, after);
  if (diff.tooLarge) return { kind: "too-large" };
  if (sameDraftText(before, after)) return { kind: "same" };
  return { kind: "rows", rows: withFoldStarts(foldUnchanged(diff.lines)) };
}

/** The words of a changed line: the ones that differ sit in `<ins>` / `<del>` and
 * between hidden markers, so they are announced even where those elements are not. */
function Words({
  segments,
  type,
}: {
  segments: readonly DiffSegment[];
  type: "added" | "removed";
}): ReactNode {
  const side = SIDE[type];
  const Tag = type === "added" ? "ins" : "del";
  return segments.map((segment, index) =>
    segment.changed ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: the segments of a line are positional; their text is not unique.
      <Fragment key={index}>
        <span className="sr-only">{`[${side.markerName}: `}</span>
        <Tag className={side.word}>{segment.text}</Tag>
        <span className="sr-only">]</span>
      </Fragment>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: the segments of a line are positional; their text is not unique.
      <Fragment key={index}>{segment.text}</Fragment>
    ),
  );
}

function Line({ line }: { line: DiffLine }): React.JSX.Element {
  if (line.type === "same") {
    return (
      <li className={ROW_CLASS}>
        <span aria-hidden="true" className={MARK_CLASS}>
          &nbsp;
        </span>
        <span className={TEXT_CLASS}>{line.text}</span>
      </li>
    );
  }
  const side = SIDE[line.type];
  const blank = line.text.trim() === "";
  return (
    <li data-change={line.type} className={cn(ROW_CLASS, side.row)}>
      <span aria-hidden="true" className={MARK_CLASS}>
        {side.mark}
      </span>
      <span className={TEXT_CLASS}>
        <span className="sr-only">{blank ? `${side.spoken}línea en blanco` : side.spoken}</span>
        {line.segments ? <Words segments={line.segments} type={line.type} /> : line.text}
      </span>
    </li>
  );
}

function Legend({
  beforeLabel,
  afterLabel,
}: { beforeLabel: string; afterLabel: string }): React.JSX.Element {
  return (
    <p className="flex flex-wrap gap-2 text-help">
      <span className={cn("rounded px-1.5", SIDE.removed.row)}>{`− ${beforeLabel}`}</span>
      <span className={cn("rounded px-1.5", SIDE.added.row)}>{`+ ${afterLabel}`}</span>
    </p>
  );
}

function TextBlock({ label, text }: { label: string; text: string }): React.JSX.Element {
  return (
    <div>
      <p className="mb-1 font-medium">{label}</p>
      <div className="whitespace-pre-wrap rounded-md border p-2 [overflow-wrap:anywhere]">
        {text}
      </div>
    </div>
  );
}

/** A text diff of `before` against `after`, in the manner of `git diff`: removed
 * and added lines, the words that changed inside a reworded line, and the long
 * unchanged runs folded. It computes once per pair of texts. */
export function DraftDiff({
  before,
  after,
  beforeLabel = "Versión anterior",
  afterLabel = "Versión nueva",
  label = "Diferencias",
}: DraftDiffProps): React.JSX.Element {
  const view = useMemo(() => viewOf(before, after), [before, after]);
  // The folds the reader opened, by where their hidden lines start in the old text, so they
  // stay open while the new text changes (someone typing) and start folded for a new old text.
  const [opened, setOpened] = useState<{ before: string; folds: ReadonlySet<number> }>({
    before,
    folds: new Set(),
  });
  const open = opened.before === before ? opened.folds : new Set<number>();
  const toggle = (start: number) => {
    const folds = new Set(open);
    if (!folds.delete(start)) folds.add(start);
    setOpened({ before, folds });
  };

  return (
    <div className="min-w-0 space-y-2">
      <Legend beforeLabel={beforeLabel} afterLabel={afterLabel} />
      {view.kind === "too-large" ? (
        <div className="space-y-3">
          <p className="text-help text-muted-foreground">
            Los textos son demasiado largos para resaltar las diferencias.
          </p>
          <TextBlock label={beforeLabel} text={before} />
          <TextBlock label={afterLabel} text={after} />
        </div>
      ) : view.kind === "same" ? (
        <p className="text-help text-muted-foreground">Sin cambios.</p>
      ) : (
        <ul
          // biome-ignore lint/a11y/noRedundantRoles: Safari with VoiceOver drops the list semantics of a list whose list-style is none, as Tailwind's preflight sets it.
          // biome-ignore lint/a11y/useSemanticElements: it is a <ul>; the role only restores what Safari drops (see above).
          role="list"
          aria-label={label}
          className="rounded-md border font-mono text-help [&>li:first-child]:rounded-t-[inherit] [&>li:last-child]:rounded-b-[inherit]"
        >
          {view.rows.map((row, index) =>
            row.kind === "line" ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: a diff is a positional list of lines; neither a line's text nor its type is unique.
              <Line key={index} line={row.line} />
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: a diff is a positional list of rows; a fold has no identity of its own.
              <Fragment key={index}>
                <li className="px-2">
                  <button
                    type="button"
                    aria-expanded={open.has(row.start)}
                    onClick={() => toggle(row.start)}
                    className="min-h-6 w-full rounded-sm text-left text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {`${open.has(row.start) ? "Ocultar" : "Mostrar"} ${row.hidden.length} líneas sin cambios`}
                  </button>
                </li>
                {open.has(row.start)
                  ? row.hidden.map((line, hiddenIndex) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: the lines of a fold are positional; their text is not unique.
                      <Line key={hiddenIndex} line={line} />
                    ))
                  : null}
              </Fragment>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

interface DraftDiffDisclosureProps {
  /** The text of the `<summary>`, for example "Ver cambios sin publicar". */
  summary: string;
  /** Mounted on the first open and kept afterwards. */
  children: ReactNode;
  className?: string;
  summaryClassName?: string;
}

/** A native `<details>` whose content is not mounted until it is first opened, so
 * a closed disclosure computes no diff and starts no request. */
export function DraftDiffDisclosure({
  summary,
  children,
  className,
  summaryClassName,
}: DraftDiffDisclosureProps): React.JSX.Element {
  const [opened, setOpened] = useState(false);
  return (
    <details
      className={className}
      onToggle={(event) => {
        if (event.currentTarget.open) setOpened(true);
      }}
    >
      <summary className={cn("cursor-pointer", summaryClassName)}>{summary}</summary>
      {opened ? children : null}
    </details>
  );
}
